/**
 * Competency Library panel — surfaced on the customer dashboard.
 *
 * Now backed by live data:
 *   • `/api/competencies/summary` — per-competency aggregate stats
 *   • `AssignCompetencyModal` — pop the assignment flow for any card
 *
 * When a competency has no assignments yet, the card shows
 * "Not assigned" — a clear invitation to click Assign.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Search,
  ChevronDown,
  ArrowRight,
  Plus,
  Users,
  TrendingUp,
  AlertTriangle,
  CalendarClock,
} from "lucide-react";
import {
  competencyCategories,
  totalModuleCount,
} from "../data/competencies";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import AssignCompetencyModal from "./AssignCompetencyModal";

/* ---------------- category accent tokens ---------------- */

const accents = {
  gold: {
    icon: "text-[#8C6B33] bg-[#FDF6E7] border-[#D4A65A]/40",
    ring: "#D4A65A",
  },
  emerald: {
    icon: "text-emerald-700 bg-emerald-50 border-emerald-200",
    ring: "#10B981",
  },
  navy: {
    icon: "text-slate-100 bg-[#0B1220] border-white/10",
    ring: "#334155",
  },
  rose: {
    icon: "text-rose-700 bg-rose-50 border-rose-200",
    ring: "#F43F5E",
  },
};

const statusStyles = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In review": "bg-[#FDF6E7] text-[#8C6B33] border-[#D4A65A]/40",
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  "Not assigned": "bg-white text-slate-400 border-slate-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/* ---------------- component ---------------- */

export default function CompetencyLibrary() {
  const { business } = useAuth();
  const isDemo = business?.is_demo;

  const [summary, setSummary] = useState({});
  const [query, setQuery] = useState("");
  // Track which categories the user has manually expanded. Empty = every
  // category collapsed. When a search is active every category behaves as
  // if it's expanded so the filtered results are visible.
  const [expanded, setExpanded] = useState({});
  const [assignFor, setAssignFor] = useState(null);
  const searchActive = query.trim().length > 0;

  async function load() {
    try {
      const data = await api.get("/competencies/summary");
      setSummary(data || {});
    } catch (e) {
      // Silent — Library still renders with "Not assigned" states.
      console.error("competency summary load failed", e);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return competencyCategories;
    return competencyCategories
      .map((cat) => ({
        ...cat,
        modules: cat.modules.filter((m) =>
          m.name.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.modules.length > 0);
  }, [query]);

  const total = totalModuleCount();

  function toggleAll(open) {
    if (open) {
      setExpanded(
        Object.fromEntries(filtered.map((c) => [c.id, true]))
      );
    } else {
      setExpanded({});
    }
  }
  const anyExpanded = filtered.some((c) => expanded[c.id]);

  return (
    <section
      data-testid="dashboard-competency-library"
      className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 md:p-8"
    >
      {/* Section header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
            <Sparkles size={12} />
            Competency Library
          </div>
          <h2 className="mt-2 font-display text-2xl md:text-[28px] tracking-[-0.02em] text-slate-900 font-semibold">
            The compliance surface CanniComply governs.
          </h2>
          <p className="mt-1.5 text-[13.5px] text-slate-500 max-w-2xl leading-relaxed">
            {total} regulated competencies grouped across{" "}
            {competencyCategories.length} disciplines. Click{" "}
            <b className="text-slate-700">Assign</b> on any module to allocate
            it to staff — completion mints a Certificate of Competency.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
          <label className="relative shrink-0 w-full md:w-64">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search competencies…"
              data-testid="competency-library-search"
              className="w-full pl-9 pr-3 py-2.5 text-[13px] rounded-lg border border-slate-200 bg-slate-50/70 focus:bg-white focus:border-[#D4A65A]/60 focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none text-slate-700 placeholder:text-slate-400 transition"
            />
          </label>
          <button
            type="button"
            onClick={() => toggleAll(!anyExpanded)}
            data-testid="competency-library-toggle-all"
            className="hidden md:inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-600 hover:text-[#0B1220] border border-slate-200 hover:border-slate-300 bg-white rounded-lg px-3 py-2 shrink-0 transition-colors"
          >
            {anyExpanded ? "Collapse all" : "Expand all"}
          </button>
          <Link
            to="/app/competencies"
            data-testid="competency-library-view-all"
            className="hidden md:inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0B1220] hover:text-[#8C6B33] shrink-0"
          >
            Manage all <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      {/* Categories */}
      <div className="mt-8 space-y-3">
        {filtered.map((cat) => {
          const Icon = cat.icon;
          const isOpen = searchActive || !!expanded[cat.id];
          const accent = accents[cat.accent] || accents.navy;
          const agg = aggregateCategory(cat, summary);

          return (
            <div
              key={cat.id}
              data-testid={`competency-category-${cat.id}`}
              className={`rounded-xl border transition-colors ${
                isOpen
                  ? "border-slate-200 bg-white"
                  : "border-slate-200 bg-slate-50/40 hover:bg-white hover:border-slate-300"
              }`}
            >
              <button
                type="button"
                onClick={() =>
                  setExpanded((c) => ({ ...c, [cat.id]: !c[cat.id] }))
                }
                aria-expanded={isOpen}
                className="w-full flex items-center gap-4 text-left group px-4 md:px-5 py-3.5"
                data-testid={`competency-category-toggle-${cat.id}`}
              >
                <span
                  className={`h-10 w-10 rounded-lg border ${accent.icon} flex items-center justify-center shrink-0`}
                >
                  <Icon size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h3 className="font-display text-[15.5px] md:text-[17px] font-semibold tracking-tight text-slate-900 truncate">
                      {cat.name}
                    </h3>
                    <span className="text-[10px] font-mono text-slate-400 tabular-nums uppercase tracking-[0.14em]">
                      {cat.modules.length}
                      {" "}
                      {cat.modules.length === 1 ? "competency" : "competencies"}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-slate-600">
                    <span
                      className="inline-flex items-center gap-1.5"
                      data-testid={`competency-category-completion-${cat.id}`}
                    >
                      <TrendingUp size={11} className="text-emerald-600" />
                      <span className="tabular-nums font-semibold text-slate-900">
                        {agg.completionPct}%
                      </span>
                      <span className="text-slate-500">complete</span>
                    </span>
                    <span
                      className="inline-flex items-center gap-1.5"
                      data-testid={`competency-category-staff-${cat.id}`}
                    >
                      <Users size={11} className="text-slate-500" />
                      <span className="tabular-nums font-semibold text-slate-900">
                        {agg.assignedStaff}
                      </span>
                      <span className="text-slate-500">
                        {agg.assignedStaff === 1 ? "assignment" : "assignments"}
                      </span>
                    </span>
                    {agg.overdue > 0 && (
                      <span
                        className="inline-flex items-center gap-1.5 text-rose-700"
                        data-testid={`competency-category-overdue-${cat.id}`}
                      >
                        <AlertTriangle size={11} />
                        <span className="tabular-nums font-semibold">
                          {agg.overdue}
                        </span>
                        <span>overdue</span>
                      </span>
                    )}
                    {agg.overdue === 0 && agg.dueSoon > 0 && (
                      <span
                        className="inline-flex items-center gap-1.5 text-amber-700"
                        data-testid={`competency-category-duesoon-${cat.id}`}
                      >
                        <CalendarClock size={11} />
                        <span className="tabular-nums font-semibold">
                          {agg.dueSoon}
                        </span>
                        <span>due soon</span>
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className="text-slate-400 group-hover:text-slate-700 transition-all shrink-0"
                  aria-hidden="true"
                  style={{
                    transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 180ms ease",
                  }}
                >
                  <ChevronDown size={16} />
                </span>
              </button>

              {isOpen && (
                <div
                  className="px-4 md:px-5 pb-5 pt-1 border-t border-slate-100 grid sm:grid-cols-2 xl:grid-cols-3 gap-3.5"
                  data-testid={`competency-category-body-${cat.id}`}
                >
                  {cat.modules.map((m, i) => (
                    <CompetencyCard
                      key={m.id}
                      module={m}
                      categoryId={cat.id}
                      accent={accent}
                      live={summary[m.id]}
                      delay={i * 0.03}
                      isDemo={isDemo}
                      onAssign={() =>
                        setAssignFor({
                          id: m.id,
                          name: m.name,
                          category_id: cat.id,
                          version: summary[m.id]?.version || "1.0",
                          pass_mark: 80,
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div
            data-testid="competency-library-empty"
            className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-10 text-center text-[13.5px] text-slate-500"
          >
            No competencies match{" "}
            <span className="font-mono text-slate-700">"{query}"</span>.
          </div>
        )}
      </div>

      {/* Foundation callout */}
      <div className="mt-8 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <span className="inline-flex h-9 w-9 rounded-lg bg-[#0B1220] text-[#D4A65A] items-center justify-center shrink-0">
          <Sparkles size={15} />
        </span>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-slate-900">
            Foundation for AI-generated learning
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">
            Once a competency is linked to your uploaded SOPs and regulatory
            guidance, CanniComply will auto-generate a structured learning
            module and a multiple-choice assessment for each assigned employee.
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[10px] uppercase tracking-[0.16em] font-semibold text-emerald-800 shrink-0">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Live
        </span>
      </div>

      {assignFor && (
        <AssignCompetencyModal
          competency={assignFor}
          isDemo={isDemo}
          onClose={() => setAssignFor(null)}
          onAssigned={() => load()}
        />
      )}
    </section>
  );
}

/* ---------------- individual card ---------------- */

function CompetencyCard({
  module: m,
  categoryId,
  accent,
  live,
  delay = 0,
  isDemo,
  onAssign,
}) {
  const hasData = !!live;
  const status = live?.status || "Not assigned";
  const assigned = live?.assigned ?? 0;
  const completion = live?.completion_pct ?? 0;
  const reviewDate = live?.next_review || null;
  const version = live?.version || "1.0";

  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.35, delay }}
      data-testid={`competency-card-${m.id}`}
      className="group rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-[0_10px_28px_-18px_rgba(11,18,32,0.22)] transition-all p-4 flex flex-col gap-3"
    >
      {/* Head — status pill + version */}
      <div className="flex items-center justify-between gap-2">
        <span
          data-testid={`competency-status-${m.id}`}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold ${statusStyles[status]}`}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: hasData ? accent.ring : "#CBD5E1" }}
          />
          {status}
        </span>
        <span className="text-[10px] font-mono text-slate-400 tabular-nums">
          v{version}
        </span>
      </div>

      {/* Title */}
      <h4 className="font-display text-[14.5px] leading-snug text-slate-900 font-semibold tracking-tight">
        {m.name}
      </h4>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Metric label="Assigned" value={`${assigned} staff`} />
        <Metric
          label="Completion"
          value={hasData ? `${completion}%` : "—"}
          strong={hasData}
        />
      </div>

      {/* Progress bar */}
      <div className="mt-1">
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          {hasData && (
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${completion}%` }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 + delay }}
              className="h-full"
              style={{
                background: `linear-gradient(to right, ${accent.ring}, #34D399)`,
              }}
            />
          )}
        </div>
      </div>

      {/* Review + Assign */}
      <div className="mt-1 pt-3 border-t border-slate-100 flex items-center justify-between text-[10.5px] gap-2">
        <div className="min-w-0">
          <div className="uppercase tracking-[0.14em] font-semibold text-slate-400">
            Next review
          </div>
          <div className="font-mono text-slate-600 tabular-nums truncate">
            {fmtDate(reviewDate)}
          </div>
        </div>
        <button
          type="button"
          onClick={onAssign}
          disabled={isDemo}
          data-testid={`competency-assign-${m.id}`}
          className="inline-flex items-center gap-1 rounded-full bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[10.5px] font-semibold px-2.5 py-1.5"
        >
          <Plus size={11} /> Assign
        </button>
      </div>
    </motion.article>
  );
}

function Metric({ label, value, strong }) {
  return (
    <div className="rounded-md bg-slate-50/70 border border-slate-100 px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-400">
        {label}
      </div>
      <div
        className={`mt-0.5 text-[13px] tabular-nums ${
          strong ? "text-slate-900 font-semibold" : "text-slate-700 font-medium"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

/* ---------------- category aggregates ---------------- */

function aggregateCategory(cat, summary) {
  const now = Date.now();
  const dueSoonMs = 30 * 86400000;
  let assignedStaff = 0;
  let completionSum = 0;
  let completionN = 0;
  let overdue = 0;
  let dueSoon = 0;
  for (const m of cat.modules) {
    const live = summary[m.id];
    if (!live) continue;
    assignedStaff += Number(live.assigned || 0);
    if (typeof live.completion_pct === "number") {
      completionSum += live.completion_pct;
      completionN += 1;
    }
    if (live.next_review) {
      const t = new Date(live.next_review).getTime();
      if (Number.isFinite(t)) {
        if (t < now) overdue += 1;
        else if (t - now < dueSoonMs) dueSoon += 1;
      }
    }
  }
  return {
    assignedStaff,
    completionPct: completionN > 0 ? Math.round(completionSum / completionN) : 0,
    overdue,
    dueSoon,
  };
}
